import { useCallback, useEffect, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import LoadingSpinner from "components/LoadingSpinner";
import { useAuthz } from "context/AuthzContext";
import { useCompany } from "context/CompanyContext";
import { listTenantUsers, listSSOAllowedEmails } from "services/tenantUserAdminService";
import PropTypes from "prop-types";
import { TC } from "../tokens/text";

function TenantUsersList({ refreshKey, onSelectUser, selectedUserId, onUsersLoaded }) {
  const { tenantId, isSuperAdmin, isTenantAdmin } = useAuthz();
  const { companies, selectedCompanyId } = useCompany();
  const [state, setState] = useState({ isLoading: false, error: "", users: [] });
  const [search, setSearch] = useState("");

  // For tenant admin: own tenant. For super admin: all tenants.
  const targetTenantId = useMemo(
    () => (isSuperAdmin ? null : tenantId || ""),
    [isSuperAdmin, tenantId]
  );

  // Build a map of tenantId → tenantName for display
  const tenantNameMap = useMemo(() => {
    const map = {};
    if (Array.isArray(companies)) {
      companies.forEach((c) => { map[c.id] = c.name || c.id; });
    }
    return map;
  }, [companies]);

  const loadUsers = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: "" }));
    try {
      if (isSuperAdmin) {
        // Load users from all tenants
        const allUsers = [];
        await Promise.all(
          (companies || []).map(async (org) => {
            try {
              const data = await listTenantUsers({ tenantId: org.id, limit: 200 });
              const users = Array.isArray(data.users) ? data.users : [];
              users.forEach((u) => {
                allUsers.push({ ...u, _tenantId: org.id, _tenantName: org.name || org.id });
              });
            } catch {
              // skip orgs that fail
            }
          })
        );

        // Fetch allowed SSO emails per tenant in parallel and build a map
        const allowedByTenant = {};
        await Promise.all(
          (companies || []).map(async (org) => {
            try {
              const resp = await listSSOAllowedEmails(org.id);
              const entries = Array.isArray(resp?.allowedEmails) ? resp.allowedEmails : [];
              const m = {};
              entries.forEach((e) => { if (e?.email) m[e.email.toLowerCase()] = e; });
              allowedByTenant[org.id] = m;
            } catch {
              // ignore
              allowedByTenant[org.id] = {};
            }
          })
        );

        // Annotate users with SSO info where available
        const annotated = allUsers.map((u) => ({
          ...u,
          _ssoEntry: allowedByTenant[u._tenantId || ""] ? allowedByTenant[u._tenantId || ""][((u.email||"").toLowerCase())] : null,
        }));

        setState({ isLoading: false, error: "", users: annotated });
        if (typeof onUsersLoaded === "function") onUsersLoaded(annotated);
      } else {
        const data = await listTenantUsers({ tenantId: targetTenantId, limit: 200 });
        const users = Array.isArray(data.users) ? data.users : [];

        // Fetch allowed emails for this tenant and build map
        let allowedEntries = {};
        try {
          const resp = await listSSOAllowedEmails(targetTenantId);
          const entries = Array.isArray(resp?.allowedEmails) ? resp.allowedEmails : [];
          entries.forEach((e) => { if (e?.email) allowedEntries[e.email.toLowerCase()] = e; });
        } catch {
          allowedEntries = {};
        }

        const annotated = users.map((u) => ({
          ...u,
          _tenantId: targetTenantId,
          _tenantName: tenantNameMap[targetTenantId] || targetTenantId,
          _ssoEntry: allowedEntries[((u.email||"").toLowerCase())] || null,
        }));

        setState({ isLoading: false, error: "", users: annotated });
        if (typeof onUsersLoaded === "function") onUsersLoaded(annotated);
      }
    } catch (error) {
      setState({ isLoading: false, error: error?.message || "Failed to load users.", users: [] });
    }
  }, [isSuperAdmin, companies, targetTenantId, tenantNameMap]);

  useEffect(() => {
    if (!isSuperAdmin && !targetTenantId) { setState((prev) => ({ ...prev, users: [] })); return; }
    if (isSuperAdmin && (!companies || companies.length === 0)) return;
    loadUsers();
  }, [loadUsers, refreshKey]);

  useEffect(() => {
    if (!selectedUserId && state.users.length > 0) {
      onSelectUser?.(state.users[0]);
    }
  }, [onSelectUser, selectedUserId, state.users]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return state.users;
    return state.users.filter(
      (u) =>
        (u.displayName || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.role || "").toLowerCase().includes(q) ||
        (u._tenantName || "").toLowerCase().includes(q)
    );
  }, [state.users, search]);

  // Group users by tenant for display
  const groupedByTenant = useMemo(() => {
    if (!isSuperAdmin) return null;
    const groups = {};
    filteredUsers.forEach((u) => {
      const key = u._tenantId || "unknown";
      if (!groups[key]) groups[key] = { name: u._tenantName || key, users: [] };
      groups[key].users.push(u);
    });
    return groups;
  }, [isSuperAdmin, filteredUsers]);

  if (!isSuperAdmin && !isTenantAdmin) {
    return (
      <Card>
        <VuiBox p={3}>
          <VuiTypography color="warning" variant="button">
            Only admins can manage users.
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  if (!isSuperAdmin && !targetTenantId) {
    return (
      <Card>
        <VuiBox p={3}>
          <VuiTypography color="warning" variant="button">
            Select a tenant before listing users.
          </VuiTypography>
        </VuiBox>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <VuiBox p={3}>
          <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2} gap={2} flexWrap="wrap">
            <VuiTypography variant="lg" sx={{ color: TC.primary }}>
              Users {state.users.length > 0 ? `(${state.users.length})` : ""}
            </VuiTypography>
            <VuiBox sx={{ minWidth: 240, flex: 1, maxWidth: 360 }}>
              <VuiInput
                placeholder={isSuperAdmin ? "Search by name, email, role or tenant..." : "Search by name, email or role..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search tenant users"
                icon={{ component: <SearchIcon />, direction: "left" }}
              />
            </VuiBox>
          </VuiBox>

          {state.error ? (
            <VuiTypography color="error" variant="button" mb={2} display="block">
              {state.error}
            </VuiTypography>
          ) : null}

          {state.isLoading ? (
            <LoadingSpinner label="Loading users…" />
          ) : filteredUsers.length === 0 ? (
            <VuiTypography variant="button" sx={{ color: TC.primary }}>
              {search ? "No users match your search." : "No users found."}
            </VuiTypography>
          ) : isSuperAdmin && groupedByTenant ? (
            Object.entries(groupedByTenant).map(([tenantKey, group]) => (
              <VuiBox key={tenantKey} mb={3}>
                <VuiTypography variant="button" fontWeight="bold" sx={{ color: TC.primary, mb: 1 }} display="block">
                  {group.name} ({group.users.length})
                </VuiTypography>
                <Grid container spacing={0}>
                  {group.users.map((user) => {
                    const userId = user.uid || user.id;
                    return (
                      <Grid item xs={12} key={userId} sx={{ mb: 0.5 }}>
                        <VuiBox
                          p={1}
                          borderRadius="lg"
                          display="flex"
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          flexDirection={{ xs: "column", md: "row" }}
                          gap={1}
                          onClick={() => onSelectUser?.(user)}
                          sx={{ border: "1px solid #d8e6f7", background: selectedUserId === userId ? "#e9f3ff" : "#f4f8ff", cursor: onSelectUser ? "pointer" : "default" }}
                        >
                          <VuiBox>
                            <VuiTypography variant="button" fontWeight="bold" display="block" sx={{ color: TC.primary }}>
                              {user.displayName || user.email}
                            </VuiTypography>
                          </VuiBox>
                        </VuiBox>
                      </Grid>
                    );
                  })}
                </Grid>
              </VuiBox>
            ))
          ) : (
            <Grid container spacing={0}>
              {filteredUsers.map((user) => {
                const userId = user.uid || user.id;

                return (
                  <Grid item xs={12} key={userId} sx={{ mb: 0.5 }}>
                    <VuiBox
                          p={1}
                          borderRadius="lg"
                          display="flex"
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                          flexDirection={{ xs: "column", md: "row" }}
                          gap={1}
                          onClick={() => onSelectUser?.(user)}
                          sx={{ border: "1px solid #d8e6f7", background: selectedUserId === userId ? "#e9f3ff" : "#f4f8ff", cursor: onSelectUser ? "pointer" : "default" }}
                        >
                          <VuiBox>
                            <VuiTypography variant="button" fontWeight="bold" display="block" sx={{ color: TC.primary }}>
                              {user.displayName || user.email}
                            </VuiTypography>
                          </VuiBox>
                        </VuiBox>
                      </Grid>
                );
              })}
            </Grid>
          )}
        </VuiBox>
      </Card>

    </>
  );
}

TenantUsersList.defaultProps = {
  refreshKey: 0,
  onSelectUser: null,
  selectedUserId: null,
  onUsersLoaded: null,
};

TenantUsersList.propTypes = {
  refreshKey: PropTypes.number,
  onSelectUser: PropTypes.func,
  selectedUserId: PropTypes.string,
  onUsersLoaded: PropTypes.func,
};

export default TenantUsersList;
